import type { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';

/**
 * The one policy for what blooms, and the closed vocabulary of reasons.
 *
 * ── Why this is a module and not four `addIncludedOnlyMesh` calls ───────────
 *
 * `GlowLayer` blooms every emissive material in the scene unless it is put in
 * include-only mode, and **"emissive" is not the same question as "is this a light"**.
 * The scene is full of surfaces that are self-lit for reasons that have nothing to do
 * with being a source:
 *
 *  - scenery cloth and skin carry an emissive lift so they read against a dark floor;
 *  - a wall's trim is a continuous line along every wall in the zone;
 *  - a portal's floor sigil shares the ring's fully-emissive material.
 *
 * Bloomed, those were a lit shopkeeper, a neon grid and a white blob — all three seen in
 * a browser the first time this was wired by emissive alone.
 *
 * The opposite fault arrived immediately after and is the reason this file exists rather
 * than a boolean on one builder: the zone renderer opted its surfaces in, and **the spell
 * effects did not**, because they are built somewhere else entirely. So a window glowed
 * and a fireball did not — the two most obviously light things in the game, disagreeing,
 * with the policy written in two places that had never met.
 *
 * Every mesh that blooms now goes through `registerGlow`, and the `GlowKind` it names is
 * the argument for why. A kind is cheap to add; a call with no kind is not expressible.
 */

/**
 * Why a surface blooms. The set is closed on purpose — see the note above.
 *
 * `world-light`  a fixture that lights the place it stands in: a window, pooled lava.
 * `spell`        something a character did: a bolt in flight, a blast ring, an impact.
 * `gateway`      a hole to somewhere else. Only the portal ring, and only the ring.
 */
export const GLOW_KINDS = ['world-light', 'spell', 'gateway'] as const;
export type GlowKind = (typeof GLOW_KINDS)[number];

/**
 * Adds a mesh to the bloom pass, or does nothing when there is no pass.
 *
 * `layer` is nullable throughout because `?glow=0` and safe mode both refuse to build
 * one, and every caller would otherwise need the same guard. The kind is unused at
 * runtime today and is not decoration: it is what makes a call site state its case, and
 * what a future per-kind intensity would key off.
 */
export function registerGlow(layer: GlowLayer | null | undefined, mesh: Mesh, kind: GlowKind): void {
  void kind;
  layer?.addIncludedOnlyMesh(mesh);
}

/**
 * What must **not** bloom, as a list of reasons rather than a list of meshes.
 *
 * Kept as prose because it is the half of the policy no code can express: these are
 * surfaces that pass every test for "emissive" and would each be a bug if lit.
 *
 *  - Character cloth and skin — an emissive *lift*, so a figure reads against a dark
 *    floor. Bloomed, a shopkeeper becomes a lamp.
 *  - `zone-trim` — the biome's signature light, and a continuous line along the top of
 *    every wall. Bloomed, a dark ruin becomes a neon grid.
 *  - The portal's floor disc — 3.2 units of the ring's own fully-emissive material.
 *    Bloomed, the gate is lost inside a white blob.
 *  - Loot beams and nameplates, if they ever become meshes: they are interface drawn in
 *    the world, and the HUD does not bloom.
 */
export const NEVER_GLOWS = Object.freeze([
  'character cloth and skin',
  'zone-trim',
  'the portal floor disc',
]);
